import { Button, Card, TextField, Typography } from '@mui/material';
import { Formik, Form, Field } from 'formik';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import * as Yup from 'yup';
import useNotification from '../../../hooks/useNotification';
import {
  getSCDetailsForDegree,
  postSCDetailsForDegree,
} from '../../../features/student/studentSlice';
import api from '../../../services/api';
import ToastNotification from '../../UIElements/ToastNotification';
import { SCIdCheck } from '../../../features/history/HistorySlice';
import PersonIcon from '@mui/icons-material/Person';

export const ValidationSchema = Yup.object({
  studentId: Yup.string().required('Student ID is required'),
  studentName: Yup.string().required('Student name is required'),
  guardianName: Yup.string().required('Guardian name is required'),
  className: Yup.string().required('Class name is required'),
  //   academicYear: Yup.string().required('Academic year is required'),
  dateOfJoining: Yup.string().required('Date of joining is required'),
  dateOfLeaving: Yup.string().required('Date of leaving is required'),
  scDate: Yup.string().required('Date of Issuing is required'),
  conduct: Yup.string().required('Conduct is required'),
});
export default function SCDegreeCollege({ StudentId = '' }) {
  const [data, setData] = useState('');
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [isIdExists, setIsIdExists] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const { notification, showSuccess, showError, hideNotification } =
    useNotification();
  async function fetchSCDetails() {
    try {
      setLoading(true);
      const res = await dispatch(getSCDetailsForDegree(StudentId)).unwrap();
      if (res.status === 200) {
        const normalizedData = Object.fromEntries(
          Object.entries(res.data).map(([key, value]) => [key, value ?? '']),
        );
        setData(normalizedData);
      }
    } catch (err) {
      console.log(err);
      setData('');
    } finally {
      setLoading(false);
    }
  }
  async function downloadSC(searchParameters) {
    try {
      setIsDownloading(true);
      const fileResponse = await api({
        baseURL: process.env.REACT_APP_API_BASE_URL,
        url: `sc/download/${StudentId}`,
        method: 'GET',
        responseType: 'blob',
        headers: searchParameters,
      });
      const blob = new Blob([fileResponse?.data], {
        type: fileResponse.headers['content-type'],
      });
      let filename = `${StudentId}-SC.pdf`;
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      showError('Error in Downloading the Study Certificate .');
      console.error('Download failed', err);
    } finally {
      setIsDownloading(false);
    }
  }
  useEffect(() => {
    if (StudentId) fetchSCDetails();
  }, [StudentId]);
  //   No Studnet Id
  if (!StudentId)
    return (
      <Card className='p-3 border'>
        <div className='flex flex-col items-center justify-center rounded-lg h-60'>
          <PersonIcon
            className='mb-1 text-gray-300 animate-pulse'
            style={{ fontSize: 90 }}
          />
          <Typography variant='h6' className='mb-2 text-gray-400'>
            No Student Selected
          </Typography>
          <Typography variant='body2' className='text-gray-500'>
            Please search by Student Id to know the student details.
          </Typography>
        </div>
      </Card>
    );
  // Loading
  if (loading)
    return (
      <Card className='relative border min-h-80'>
        <Spinner
          asOverlay={true}
          text={`Fetching the TC  Details of ${StudentId}`}
        />
      </Card>
    );
  // No Data
  if (!data)
    return (
      <Card className='flex items-center justify-center border rounded-lg h-60'>
        <Typography variant='body1'>Student details not found</Typography>
      </Card>
    );
  // Actual Component
  return (
    <div>
      <Formik
        initialValues={data}
        validationSchema={ValidationSchema}
        enableReinitialize
        validateOnChange={true}
        validateOnBlur={true}
        onSubmit={async (values) => {
          try {
            setIsSubmitting(true);
            const resp = await dispatch(
              postSCDetailsForDegree(values),
            ).unwrap();
            if (resp.status === 200) {
              setData(resp.data);
              showSuccess('Study Certificate details submitted successfully.');
            }
          } catch (error) {
            console.log(error);
            showError('Error in submitting the details.');
          } finally {
            setIsSubmitting(false);
          }
        }}
      >
        {({ values }) => (
          <Form>
            <div className='relative  mx-auto  border p-8 rounded-md bg-white'>
              {/* TOP */}
              {(isSubmitting || isDownloading) && (
                <div className='absolute inset-0 z-10 flex items-center justify-center bg-white/80 opacity-80'>
                  <span className='text-lg font-semibold '>
                    {isSubmitting ? 'Submitting...' : 'Downloading...'}
                  </span>
                </div>
              )}
              <div className='flex flex-col   text-center'>
                <h2 className='font-bold'>BELLAMKONDA DEGREE COLLEGE</h2>
                <h6>
                  <b className='font-bold'>C.S.PURAM - 523112</b>,Prakasam Dist.
                  Pin:523112
                </h6>
                <div className='flex justify-center mt-2'>
                  {' '}
                  <h4 className='border-2 p-1 rounded-lg w-fit border-black font-bold'>
                    STUDY CUM CONDUCT CERTIFICATE
                  </h4>
                </div>
              </div>
              <div className='flex justify-between mt-6 mb-3 flex-wrap gap-2 text-[20px] leading-10'>
                {/* Addmission Number */}
                <div className='flex gap-1 italic'>
                  Ad.No/Std Id :{' '}
                  {data?.studentId ? (
                    <b className='text-red-500'> {data?.studentId}</b>
                  ) : (
                    <div>
                      <div className='flex gap-2 mr-2'>
                        <Field name='studentId'>
                          {({ field, meta }) => (
                            <TextField
                              size='small'
                              className='w-40'
                              variant='standard'
                              {...field} // Spreads name, value, onChange, and onBlur
                              InputProps={{
                                sx: {
                                  '& input:-webkit-autofill': {
                                    WebkitBoxShadow: '0 0 0 100px white inset',
                                  },
                                },
                              }}
                              fullWidth
                              // Error handling:
                              error={meta.touched && Boolean(meta.error)}
                              helperText={meta.touched && meta.error}
                              onChange={(e) => {
                                field.onChange(e);
                                const value = e.target.value;
                                if (!value) setIsIdExists(false);
                              }}
                              onBlur={async (e) => {
                                field.onBlur(e);
                                if (e.target.value) {
                                  const res = await dispatch(
                                    SCIdCheck(e.target.value),
                                  ).unwrap();
                                  setIsIdExists(res);
                                } else {
                                  setIsIdExists(false);
                                }
                              }}
                            />
                          )}
                        </Field>
                      </div>
                      {isIdExists && (
                        <div
                          id='transactionIdError'
                          className='mb-2 text-red-600 text-sm text-center'
                        >
                          Admission No already Exists
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {/* Date of SC Issuing Date */}
                <div className='flex gap-4 italic'>
                  <span className='text-nowrap italic'>Date : </span>{' '}
                  <DateField name='scDate' Tailwindwidth='w-40' />
                </div>
              </div>
              {/* SC Body div */}
              <div className='font-serif bg-white text-[20px] leading-10 '>
                {/*Row 1*/}
                <div className='flex items-center gap-2 italic'>
                  <span className=' text-nowrap'>This is to certify that</span>{' '}
                  <InputField name='studentName' />
                </div>
                {/* Row 2 */}
                <div className='italic flex items-center gap-2'>
                  <span className='text-nowrap'>S/o, D/o, Sri</span>
                  <InputField name='guardianName' />
                  <span className='text-nowrap'>is studying/studied</span>
                </div>
                {/* Row 3 */}
                <div className='italic flex items-center gap-2'>
                  <span className='text-nowrap'>
                    Has Studied in the college in the
                  </span>
                  <InputField name='className' />
                </div>
                {/* Row 4 */}
                <div className='italic flex  items-center gap-2'>
                  <span>From</span>
                  <InputField name='dateOfJoining' className='ml-4' />
                  <span className='ml-4'>to</span>
                  <InputField name='dateOfLeaving' className='ml-4' />
                </div>
                {/* Row 5 */}
                <div className='italic flex'>
                  <span>And that during this time.</span>
                </div>
                {/* Row 6 */}
                <div className='italic flex mt-4 gap-2'>
                  <span className='text-nowrap'>His/Her conduct has been</span>
                  <InputField name='conduct' className='w-40' />
                </div>
                {/* Signature */}
                <div className='flex justify-end mr-32'>
                  <span className='italic'>Principal</span>
                </div>
              </div>

              <div className='flex justify-between gap-12 mt-6'>
                <Button
                  variant='contained'
                  color='error'
                  type='button'
                  disabled={!values.id || isDownloading}
                  onClick={downloadSC}
                >
                  Print/Download
                </Button>{' '}
                <Button
                  variant='contained'
                  type='submit'
                  disabled={isIdExists || isSubmitting}
                >
                  Submit
                </Button>
              </div>
            </div>{' '}
          </Form>
        )}
      </Formik>
      <ToastNotification
        open={notification.open}
        onClose={hideNotification}
        message={notification.message}
        title={notification.title}
        severity={notification.severity}
        autoHideDuration={notification.autoHideDuration}
        position={notification.position}
      />
    </div>
  );
}

function InputField({ name, disabled = false, ...props }) {
  return (
    <Field name={name}>
      {({ field, meta }) => (
        <TextField
          {...props}
          size='small'
          disabled={disabled}
          variant='standard'
          {...field} // Spreads name, value, onChange, and onBlur
          fullWidth
          InputProps={{
            sx: {
              '& input:-webkit-autofill': {
                WebkitBoxShadow: '0 0 0 100px white inset',
              },
              '& input': {
                fontFamily: '"Times New Roman", serif',
                fontStyle: 'italic',
                fontWeight: 650,
                fontSize: '20px',
                textAlign: 'center',
              },
            },
          }}
          error={meta.touched && Boolean(meta.error)}
          helperText={meta.touched && meta.error}
        />
      )}
    </Field>
  );
}
import { useRef } from 'react';
import Spinner from '../../UIElements/Spinner';

function DateField({ name, disabled = false, Tailwindwidth = 'w-full' }) {
  const inputRef = useRef(null);

  const openPicker = () => {
    if (disabled) return;

    // Modern browsers
    if (inputRef.current?.showPicker) {
      inputRef.current.showPicker();
    } else {
      // Fallback
      inputRef.current.focus();
    }
  };

  return (
    <Field name={name}>
      {({ field, form, meta }) => (
        <div className='relative w-full font-sans'>
          {/* Hidden Actual Date Input */}
          <input
            ref={inputRef}
            type='date'
            disabled={disabled}
            {...field}
            onChange={(e) => {
              form.setFieldValue(name, e.target.value);
            }}
            className='absolute opacity-0 pointer-events-none'
          />

          {/* Clickable Dashed Line */}
          <div
            onClick={openPicker}
            className={`
              border-b border-gray-500 font-semibold
              min-h-[32px]
              cursor-pointer
              flex items-center justify-center
              px-1
              italic ${Tailwindwidth}
            `}
          >
            {field?.value
              ? new Date(field.value).toLocaleDateString('en-GB')
              : ''}
          </div>

          {/* Error */}
          {meta.touched && meta.error && (
            <div className='text-[#d32f2f] text-sm mt-1'>{meta.error}</div>
          )}
        </div>
      )}
    </Field>
  );
}
